import { type AssetType, type IssueType, type ProjectFile } from "@overmap-ai/common"
import { deleteAssetType, deleteIssueType, deleteProjectFile, persistSliceReducer, RESET_STATE } from "@overmap-ai/core"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { DateRange } from "react-day-picker"

import { AssetMapColorMode } from "../../enums/assets"
import { MapStyle } from "../../mapbox/enums"
import { APP_DATA_DB_NAME } from "../constants"
import { setActiveProjectId } from "./AppSlice"

export interface MapVisibilitySliceState {
	hiddenAssetTypeIds: Record<AssetType["uuid"], boolean>
	hiddenIssueTypeIds: Record<IssueType["uuid"], boolean>
	hiddenProjectFileIds: Record<ProjectFile["uuid"], boolean>
	geoImageDateRange: DateRange | null
	// Other
	assetMapColorMode: AssetMapColorMode
	assetGeometryFillOpacity: number
	assetGeometryStrokeOpacity: number
	showAssetLabelsByAssetTypeId: Record<AssetType["uuid"], boolean>
	mapStyle: MapStyle
	showProjectBounds: boolean
}

const initialState: MapVisibilitySliceState = {
	hiddenAssetTypeIds: {},
	hiddenIssueTypeIds: {},
	hiddenProjectFileIds: {},
	geoImageDateRange: null,
	// Other
	assetMapColorMode: AssetMapColorMode.LAST_COMPLETED_STEP,
	assetGeometryFillOpacity: 0.6,
	assetGeometryStrokeOpacity: 1,
	showAssetLabelsByAssetTypeId: {},
	mapStyle: MapStyle.LIGHT,
	showProjectBounds: false,
}

export const mapVisibilitySlice = createSlice({
	name: "mapVisibility",
	initialState,
	extraReducers: (builder) =>
		builder
			.addCase(RESET_STATE, (state) => Object.assign(state, initialState))
			.addCase(setActiveProjectId, (state) => Object.assign(state, initialState))
			.addCase(deleteIssueType, (state, action) => {
				delete state.hiddenIssueTypeIds[action.payload]
			})
			.addCase(deleteProjectFile, (state, action) => {
				delete state.hiddenProjectFileIds[action.payload]
			})
			.addCase(deleteAssetType, (state, action) => {
				delete state.hiddenAssetTypeIds[action.payload]
				delete state.showAssetLabelsByAssetTypeId?.[action.payload]
			}),
	reducers: {
		// Asset types
		hideAssetType: (state, action: PayloadAction<AssetType["uuid"]>) => {
			state.hiddenAssetTypeIds[action.payload] = true
		},
		unhideAssetType: (state, action: PayloadAction<AssetType["uuid"]>) => {
			delete state.hiddenAssetTypeIds[action.payload]
		},
		setHiddenAssetTypeIds: (state, action: PayloadAction<AssetType["uuid"][]>) => {
			state.hiddenAssetTypeIds = {}
			for (const assetTypeId of action.payload) {
				state.hiddenAssetTypeIds[assetTypeId] = true
			}
		},

		// Issue Types
		hideIssueType: (state, action: PayloadAction<IssueType["uuid"]>) => {
			state.hiddenIssueTypeIds[action.payload] = true
		},
		unhideIssueType: (state, action: PayloadAction<IssueType["uuid"]>) => {
			delete state.hiddenIssueTypeIds[action.payload]
		},
		setHiddenIssueTypeIds: (state, action: PayloadAction<IssueType["uuid"][]>) => {
			state.hiddenIssueTypeIds = {}
			for (const issueTypeId of action.payload) {
				state.hiddenIssueTypeIds[issueTypeId] = true
			}
		},
		// Project Files
		showProjectFile: (state, action: PayloadAction<ProjectFile["uuid"]>) => {
			delete state.hiddenProjectFileIds[action.payload]
		},
		hideProjectFile: (state, action: PayloadAction<ProjectFile["uuid"]>) => {
			state.hiddenProjectFileIds[action.payload] = true
		},
		setHiddenProjectFileIds: (state, action: PayloadAction<ProjectFile["uuid"][]>) => {
			state.hiddenProjectFileIds = {}
			for (const fileId of action.payload) {
				state.hiddenProjectFileIds[fileId] = true
			}
		},
		// Geo Images
		setGeoImageDateRange: (state, action: PayloadAction<DateRange | null>) => {
			state.geoImageDateRange = action.payload
		},

		setAssetMapColorMode: (state, action: PayloadAction<AssetMapColorMode>) => {
			state.assetMapColorMode = action.payload
		},
		setAssetGeometryFillOpacity: (state, action: PayloadAction<number>) => {
			state.assetGeometryFillOpacity = action.payload
		},
		setAssetGeometryStrokeOpacity: (state, action: PayloadAction<number>) => {
			state.assetGeometryStrokeOpacity = action.payload
		},
		setMapStyle: (state, action: PayloadAction<MapStyle>) => {
			state.mapStyle = action.payload
		},
		setShowProjectBounds: (state, action: PayloadAction<boolean>) => {
			state.showProjectBounds = action.payload
		},
		setShowAssetLabels: (state, action: PayloadAction<{ assetTypeId: AssetType["uuid"]; showLabels: boolean }>) => {
			// Older persisted state does not contain per-type label preferences.
			state.showAssetLabelsByAssetTypeId ??= {}
			state.showAssetLabelsByAssetTypeId[action.payload.assetTypeId] = action.payload.showLabels
		},
	},
})

export const {
	// Asset types
	hideAssetType,
	unhideAssetType,
	setHiddenAssetTypeIds,

	// Issue Types
	hideIssueType,
	unhideIssueType,
	setHiddenIssueTypeIds,

	// Project Files
	hideProjectFile,
	showProjectFile,
	setHiddenProjectFileIds,

	// Geo Images
	setGeoImageDateRange,

	// Other
	setShowAssetLabels,
	setAssetMapColorMode,
	setAssetGeometryFillOpacity,
	setAssetGeometryStrokeOpacity,
	setMapStyle,
	setShowProjectBounds,
} = mapVisibilitySlice.actions

export const mapVisibilityReducer = persistSliceReducer(mapVisibilitySlice, APP_DATA_DB_NAME)
